import React, { useState, useRef, useEffect } from "react";
import './MathPractice.css';
import { awardToken } from "../../awardtoken";
import { db } from "../../firebase";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { useAuth } from "../../components/useAuth";


const UNITS = {
  distance: [["km", "m", 1000], ["m", "cm", 100], ["cm", "mm", 10]],
  weight: [["kg", "g", 1000], ["g", "mg", 1000]],
  currency: [["USD", "CAD", 1.35], ["CAD", "USD", 1/1.35]],
  temperature: [["C", "F", "CtoF"], ["F", "C", "FtoC"]]
};

// Conversion info for each type (for display)
const CONVERSION_INFO = {
  distance: [
    { from: "km", to: "m", formula: "× 1000" },
    { from: "m", to: "cm", formula: "× 100" },
    { from: "cm", to: "mm", formula: "× 10" }
  ],
  weight: [
    { from: "kg", to: "g", formula: "× 1000" },
    { from: "g", to: "mg", formula: "× 1000" }
  ],
  currency: [
    { from: "USD", to: "CAD", formula: "× 1.35" },
    { from: "CAD", to: "USD", formula: "× 0.741" }
  ],
  temperature: [
    { from: "°C", to: "°F", formula: "(°C × 9/5) + 32" },
    { from: "°F", to: "°C", formula: "(°F − 32) × 5/9" }
  ]
};

function randomConversion(type) {
  const pool = UNITS[type];
  const [from, to, factor] = pool[Math.floor(Math.random() * pool.length)];
  let value, question, answer;
  if (type === "temperature") {
    if (factor === "CtoF") {
      value = Math.floor(Math.random() * 30 + 1);
      question = `Convert ${value}°C to °F`;
      answer = value * 9 / 5 + 32;
    } else {
      value = Math.floor(Math.random() * 60 + 32);
      question = `Convert ${value}°F to °C`;
      answer = (value - 32) * 5 / 9;
    }
  } else {
    value = Math.floor(Math.random() * 20 + 2);
    question = `Convert ${value} ${from} to ${to}`;
    answer = value * factor;
  }
  return { question, answer, from, to, value };
}

function generateWorksheet(type, n) {
  const qs = [];
  for (let i = 0; i < n; i++) {
    qs.push(randomConversion(type));
  }
  return qs;
}

const TOOL_TYPE = "MathMetricConversionPractice";

async function logConversionPractice({ user, type, correct, total, startTime, endTime }) {
  if (!user?.uid) {
    console.warn("No user UID! User:", user);
    return;
  }
  if (total === 0) {
    console.warn("No attempts, nothing to log.");
    return;
  }
  try {
    await setDoc(
      doc(db, "users", user.uid, "practiceRecords", TOOL_TYPE),
      {
        toolType: TOOL_TYPE,
        topic: `${type} conversion`,
        numQuestions: total,
        correct,
        attempts: total,
        timestamp: serverTimestamp(),
        startedAt: startTime,
        finishedAt: endTime,
        durationSec: Math.round((endTime - startTime) / 1000),
      },
      { merge: true }
    );
    console.log("Metric conversion session log successfully written to Firestore.");
  } catch (e) {
    console.error("Error writing metric conversion session log:", e);
  }
}


export default function MetricConversionPractice() {
  const [type, setType] = useState("distance");
  const [q, setQ] = useState(() => randomConversion("distance"));
  const [input, setInput] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [waitingForNext, setWaitingForNext] = useState(false);
  const { user } = useAuth();
  const [sessionStart, setSessionStart] = useState(Date.now());
  const sessionStats = useRef({ total: 0, correct: 0 });
  const didMountRef = useRef(false);

  useEffect(() => {
  if (!didMountRef.current) {
    didMountRef.current = true;
    setQ(randomConversion(type));
    return;
  }
  const endTime = Date.now();
  if (sessionStats.current.total > 0) {
    logConversionPractice({
      user,
      type,
      correct: sessionStats.current.correct,
      total: sessionStats.current.total,
      startTime: sessionStart,
      endTime,
    });
  }
  setSessionStart(Date.now());
  setTotal(0);
  setCorrect(0);
  sessionStats.current = { total: 0, correct: 0 };
  setQ(randomConversion(type));
  setInput("");
  setFeedback(null);
  setWaitingForNext(false);
  // eslint-disable-next-line
}, [type]);


  // Score state
  const [correct, setCorrect] = useState(0);
  const [total, setTotal] = useState(0);
  // Token award tracking (do NOT award more than once for the same milestone)
  const tokensAwarded = useRef(0);

  // Worksheet state
  const [worksheet, setWorksheet] = useState(null);

function checkAnswer() {
  if (waitingForNext) return;
  const userInput = parseFloat(input);
  const isOk = Math.abs(userInput - q.answer) < 0.01 * Math.abs(q.answer) + 0.2;
  const nextTotal = total + 1;
  const nextCorrect = isOk ? correct + 1 : correct;
  setTotal(nextTotal);
  setCorrect(nextCorrect);
  sessionStats.current = { total: nextTotal, correct: nextCorrect };

  // Log every answer—standardize with other modules
  const endTime = Date.now();
  logConversionPractice({
    user,
    type,
    correct: nextCorrect,
    total: nextTotal,
    startTime: sessionStart,
    endTime,
  });

  if (isOk) {
    setFeedback("✅ Correct!");
    setTimeout(() => {
      setQ(randomConversion(type));
      setInput("");
      setFeedback(null);
    }, 650);
  } else {
    setFeedback(`❌ The answer is ${q.answer.toFixed(2)}`);
    setWaitingForNext(true);
  }
}

  // Printable worksheet
  if (worksheet) {
    return (
      <div className="math-practice-card worksheet">
        <h2 className="worksheet-title">Metric Conversion Worksheet</h2>
        <div className="worksheet-subtitle">{type.charAt(0).toUpperCase() + type.slice(1)} Conversion</div>
        <ol style={{ margin: "0 0 1.5em 2em" }}>
          {worksheet.map((wq, idx) => (
            <li key={idx} style={{ margin: "1.1em 0", fontSize: "1.13em" }}>
              {wq.question.replace(/\?$/, "_______")}
            </li>
          ))}
        </ol>
        <button className="no-print math-pill-btn" style={{ marginTop: 10 }} onClick={() => setWorksheet(null)}>
          Back to Practice
        </button>
      </div>
    );
  }

  // Interactive UI
  return (
    <div className="math-conversion-layout">
      <div className="math-practice-card metric-conversion-practice">
        <h2 className="math-practice-title">Metric Conversion Practice</h2>
        <label className="math-practice-label">
          Type:{" "}
          <select
            value={type}
            onChange={e => {
              setType(e.target.value);
              setQ(randomConversion(e.target.value));
              setInput(""); setFeedback(null);
            }}
          >
            <option value="distance">Distance</option>
            <option value="weight">Weight</option>
            <option value="currency">Currency</option>
            <option value="temperature">Temperature</option>
          </select>
        </label>
<div className="math-practice-question">{q.question}</div>
{feedback && <p className="math-practice-feedback">{feedback}</p>}
<div className="math-practice-input-row">
  <input
    type="number"
    value={input}
    onChange={e => setInput(e.target.value)}
    placeholder={`Answer in ${q.to || "units"}`}
    className="math-practice-input"
    disabled={waitingForNext}
  />
  <button className="math-pill-btn" onClick={checkAnswer} disabled={waitingForNext}>Check</button>
  <button
    className="math-pill-btn"
    onClick={() => {
      setQ(randomConversion(type));
      setInput("");
      setFeedback(null);
      setWaitingForNext(false);
    }}
    disabled={!waitingForNext}
  >
    Next
  </button>
</div>

        {/* Score Display */}
        <div style={{ fontWeight: 700, fontSize: "1.15em", margin: "14px 0 6px 0" }}>
          Score: {correct} / {total}
        </div>
        <button
  className="math-pill-btn"
  style={{ margin: "1em 0" }}
  onClick={() => {
    const endTime = Date.now();
    if (sessionStats.current.total > 0) {
      logConversionPractice({
        user,
        type,
        correct: sessionStats.current.correct,
        total: sessionStats.current.total,
        startTime: sessionStart,
        endTime,
      });
      setSessionStart(Date.now());
      setTotal(0);
      setCorrect(0);
      sessionStats.current = { total: 0, correct: 0 };
    }
  }}
>
  Finish Session
</button>


        {feedback && <p className="math-practice-feedback">{feedback}</p>}

        {/* Printable worksheet buttons */}
        <div className="worksheet-buttons no-print">
          <div className="worksheet-label">Printable worksheets:</div>
          <div className="worksheet-btn-row">
            {[10, 25, 50].map(n => (
              <button
                key={n}
                className="math-pill-btn"
                onClick={() => {
                  setWorksheet(generateWorksheet(type, n));
                  setTimeout(() => window.print(), 60);
                }}
              >
                {n} Questions
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* --- Conversion Info Panel --- */}
      <div className="conversion-info-panel no-print">
        <h3>Conversion Rates</h3>
        <table>
          <tbody>
            {CONVERSION_INFO[type].map((item, idx) => (
              <tr key={idx}>
                <td style={{ padding: "0 7px 0 0", fontWeight: 600 }}>
                  {item.from} → {item.to}
                </td>
                <td style={{ color: "#009", fontFamily: "monospace" }}>{item.formula}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
